import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {DataGeneratorService} from "@/angular-blocworx/components/fields/data-generator/data-generator.service";

interface RecurrenceModel {
    freq: string;
    interval: number;
    byday: string[];
    bymonthday: number[];
    bymonth: number[];
    byweekno: number[];
    bysetpos: number[];
    wkst: string;
    nthBusinessDay: boolean;
    customRrule: string;
}

@Component({
    selector: 'app-recurring-data-dialog',
    templateUrl: 'angular-blocworx/components/fields/data-generator/templates/recurring-data-dialog.component.html',
    styleUrls: []
})
export class RecurringDataDialogComponent {
    // Original model structure preserved
    model: RecurrenceModel;

    // New properties for Google-style interface
    selectedRecurrence: string = 'none';
    endsOption: string = 'never';
    endDate: Date | null = null;
    occurrences: number = 1;
    customInterval: number = 1;
    customFrequency: string = 'week';
    baseDate: Date = new Date(); // Set this to the event's start date
    isLoading: boolean = false;

    weekdayButtons = [
        { short: 'S', full: 'Sunday', value: 'SU', selected: false },
        { short: 'M', full: 'Monday', value: 'MO', selected: false },
        { short: 'T', full: 'Tuesday', value: 'TU', selected: false },
        { short: 'W', full: 'Wednesday', value: 'WE', selected: false },
        { short: 'T', full: 'Thursday', value: 'TH', selected: false },
        { short: 'F', full: 'Friday', value: 'FR', selected: false },
        { short: 'S', full: 'Saturday', value: 'SA', selected: false }
    ];

    // Original arrays preserved for compatibility
    frequencies = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY', 'HOURLY'];
    weekdays = [
        { value: 'MO', label: 'Monday'    },
        { value: 'TU', label: 'Tuesday'   },
        { value: 'WE', label: 'Wednesday' },
        { value: 'TH', label: 'Thursday'  },
        { value: 'FR', label: 'Friday'    },
        { value: 'SA', label: 'Saturday'  },
        { value: 'SU', label: 'Sunday'    }
    ];
    wkstOptions = [
        { value: 'MO', label: 'Monday' },
        { value: 'SU', label: 'Sunday' }
    ];

    constructor(
        @Inject(MatDialogRef) public dialogRef: MatDialogRef<RecurringDataDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        @Inject(DataGeneratorService) private dataGeneratorService: DataGeneratorService,
    ) {
        const isInEditMode = this.data.scanStationObj?.isInEditMode === true;
        const existingRecurringData = this.data.scanStationObj?.recurringData;

        // Set base date based on the three scenarios
        if (isInEditMode && existingRecurringData && existingRecurringData.model) {
            // Scenario 1: Edit mode + recurring data exists = use base date from recurringData
            this.baseDate = existingRecurringData.baseDate ? new Date(existingRecurringData.baseDate) : this.parseDateFromData();
            console.log('Base date (Scenario 1 - Edit mode with existing recurring data):', this.baseDate);

            // Clean up the existing data (remove original_record_id)
            const cleanedData = { ...existingRecurringData };
            delete cleanedData.original_record_id;

            // Initialize from existing recurring data
            this.initializeFromExistingData(cleanedData);
        } else {
            // Scenario 2: Edit mode + no recurring data = use base date from dataToAdd
            // Scenario 3: Not in edit mode = use base date from dataToAdd
            this.baseDate = this.parseDateFromData();
            console.log('Base date (Scenario 2/3 - from dataToAdd):', this.baseDate);

            // Initialize model with default data
            this.model = {
                freq:     this.data.freq           ?? 'WEEKLY',
                interval: this.data.interval       ?? 1,
                byday:    this.data.byday          ?? [],
                bymonthday: this.data.bymonthday   ?? [],
                bymonth:    this.data.bymonth      ?? [],
                byweekno:   this.data.byweekno     ?? [],
                bysetpos:   this.data.bysetpos     ?? [],
                wkst:       this.data.wkst         ?? 'MO',
                nthBusinessDay: this.data.nthBusinessDay ?? false,
                customRrule:   this.data.customRrule    ?? ''
            };

            if (isInEditMode && existingRecurringData && existingRecurringData.model) {
                // This case is handled above, but kept for clarity
            } else {
                // Reset to defaults for new recurring data or edit mode without recurring data
                this.resetToDefaults();
            }
        }
    }

    private resetToDefaults(): void {
        // Reset all interface properties to defaults
        this.selectedRecurrence = 'none';
        this.endsOption = 'never';
        this.endDate = null;
        this.occurrences = 1;
        this.customInterval = 1;
        this.customFrequency = 'week';

        // Reset weekday buttons
        this.weekdayButtons.forEach(btn => {
            btn.selected = false;
        });

        // Reset model arrays
        this.model.byday = [];
        this.model.bymonthday = [];
        this.model.bymonth = [];
        this.model.byweekno = [];
        this.model.bysetpos = [];
        this.model.customRrule = '';
    }

    private initializeFromExistingData(recurringData: any): void {
        // Set the model from existing data
        this.model = { ...recurringData.model };

        // Note: baseDate is already set in constructor based on edit mode logic

        // Set interface properties from existing data
        if (recurringData.interface) {
            this.selectedRecurrence = recurringData.interface.selectedRecurrence || 'none';
            this.endsOption = recurringData.interface.endsOption || 'never';
            this.endDate = recurringData.interface.endDate ? new Date(recurringData.interface.endDate) : null;
            this.occurrences = recurringData.interface.occurrences || 1;
            this.customInterval = recurringData.interface.customInterval || 1;
            this.customFrequency = recurringData.interface.customFrequency || 'week';
        }

        // Initialize weekday buttons from model
        this.weekdayButtons.forEach(btn => {
            btn.selected = this.model.byday.includes(btn.value);
        });
    }

    private parseDateFromData(): Date {
        try {
            const dateString = this.data.dataToAdd[this.data.primaryDateField.field_slug];
            const phpFormat = this.data.field.recurringDataPrimaryDateFieldFormat;

            console.log('Parsing date - dateString:', dateString, 'phpFormat:', phpFormat);

            if (!dateString || !phpFormat) {
                return new Date();
            }

            return this.parseFormattedDate(dateString, phpFormat);
        } catch (error) {
            console.error('Error parsing date:', error);
            return new Date();
        }
    }

    private parseFormattedDate(dateString: string, phpFormat: string): Date {
        // Handle d/m/Y format (19/07/2025)
        if (phpFormat === 'd/m/Y') {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
				const date = new Date(year, month, day, 12, 0, 0);
				return date;

            }
        }

        // Handle m/d/Y format (07/19/2025)
        if (phpFormat === 'm/d/Y') {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const month = parseInt(parts[0], 10) - 1;
                const day = parseInt(parts[1], 10);
                const year = parseInt(parts[2], 10);
				const date = new Date(year, month, day, 12, 0, 0);
				return date;
            }
        }

        // Handle Y-m-d format (2025-07-19)
        if (phpFormat === 'Y-m-d') {
            const parts = dateString.split('-');
            if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const day = parseInt(parts[2], 10);
				const date = new Date(year, month, day, 12, 0, 0);
				return date;
            }
        }

        // Fallback
        const parsedDate = new Date(dateString);
        return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    }

    private initializeFromModel(): void {
        // Set initial selection based on existing model
        if (this.model.customRrule) {
            this.selectedRecurrence = 'custom';
        } else if (this.model.freq === 'DAILY') {
            this.selectedRecurrence = 'daily';
        } else if (this.model.freq === 'WEEKLY') {
            if (this.model.byday.length === 5 &&
                ['MO', 'TU', 'WE', 'TH', 'FR'].every(day => this.model.byday.includes(day))) {
                this.selectedRecurrence = 'weekdays';
            } else {
                this.selectedRecurrence = 'weekly';
            }
        } else if (this.model.freq === 'MONTHLY') {
            this.selectedRecurrence = 'monthly';
        } else if (this.model.freq === 'YEARLY') {
            this.selectedRecurrence = 'annually';
        }

        // Initialize weekday buttons from model
        this.weekdayButtons.forEach(btn => {
            btn.selected = this.model.byday.includes(btn.value);
        });

        // Parse RRULE to set end conditions if they exist
        this.parseRRuleForEndConditions();
    }

    private parseRRuleForEndConditions(): void {
        if (!this.model.customRrule) return;

        const rrule = this.model.customRrule.replace('RRULE:', '');
        const parts = rrule.split(';');

        for (const part of parts) {
            const [key, value] = part.split('=');

            if (key === 'COUNT') {
                this.endsOption = 'after';
                this.occurrences = parseInt(value, 10);
            } else if (key === 'UNTIL') {
                this.endsOption = 'on';
                // Parse UNTIL date (format: YYYYMMDDTHHMMSSZ)
                const dateStr = value.replace('T235959Z', '');
                if (dateStr.length === 8) {
                    const year = parseInt(dateStr.substring(0, 4), 10);
                    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
                    const day = parseInt(dateStr.substring(6, 8), 10);
                    this.endDate = new Date(year, month, day);
                }
            } else if (key === 'INTERVAL') {
                this.customInterval = parseInt(value, 10);
            }
        }
    }

    getDayName(): string {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[this.baseDate.getDay()];
    }

    getOrdinalDay(): string {
        const dayOfMonth = this.baseDate.getDate();
        const dayName = this.getDayName();

        // Calculate which occurrence of the weekday this is in the month
        const firstOfMonth = new Date(this.baseDate.getFullYear(), this.baseDate.getMonth(), 1);
        const firstWeekday = firstOfMonth.getDay();
        const targetWeekday = this.baseDate.getDay();

        // Find the first occurrence of this weekday in the month
        let firstOccurrence = 1;
        if (targetWeekday >= firstWeekday) {
            firstOccurrence = 1 + (targetWeekday - firstWeekday);
        } else {
            firstOccurrence = 1 + (7 - firstWeekday + targetWeekday);
        }

        // Calculate which occurrence this date is
        const occurrence = Math.floor((dayOfMonth - firstOccurrence) / 7) + 1;

        // Check if this is the last occurrence of this weekday in the month
        const nextWeek = new Date(this.baseDate);
        nextWeek.setDate(dayOfMonth + 7);
        const isLast = nextWeek.getMonth() !== this.baseDate.getMonth();

        if (isLast && occurrence >= 4) {
            return `last ${dayName}`;
        }

        const ordinals = ['', 'first', 'second', 'third', 'fourth', 'fifth'];
        return `${ordinals[occurrence]} ${dayName}`;
    }

    getMonthDay(): string {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        const month = months[this.baseDate.getMonth()];
        const day = this.baseDate.getDate();

        return `${month} ${day}`;
    }

    onRecurrenceChange(event: any): void {
        const value = event.value;
        this.selectedRecurrence = value;

        // Update the model based on selection
        this.updateModelFromSelection(value);

        // Reset custom options when switching away from custom
        if (value !== 'custom') {
            this.customInterval = 1;
            this.customFrequency = 'week';
            this.resetWeekdaySelection();
        }

        // Set default weekday selection for weekly custom
        if (value === 'custom') {
            const currentDayValue = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][this.baseDate.getDay()];
            const dayBtn = this.weekdayButtons.find(btn => btn.value === currentDayValue);
            if (dayBtn) dayBtn.selected = true;
        }

        // Reset ends option when switching to "Does not repeat"
        if (value === 'none') {
            this.endsOption = 'never';
            this.endDate = null;
            this.occurrences = 1;
        }
    }

    private updateModelFromSelection(value: string): void {
        // Reset model completely first
        this.model.byday = [];
        this.model.freq = '';
        this.model.interval = 1;
        this.model.bymonthday = [];
        this.model.bymonth = [];
        this.model.byweekno = [];
        this.model.bysetpos = [];
        this.model.customRrule = '';

        const currentDayValue = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][this.baseDate.getDay()];

        switch (value) {
            case 'none':
                // Explicitly clear everything for "Does not repeat"
                this.model.freq = '';
                this.model.interval = 0;
                this.model.customRrule = '';
                break;
            case 'daily':
                this.model.freq = 'DAILY';
                break;
            case 'weekly':
                this.model.freq = 'WEEKLY';
                this.model.byday = [currentDayValue];
                break;
            case 'monthly':
                this.model.freq = 'MONTHLY';
                break;
            case 'annually':
                this.model.freq = 'YEARLY';
                break;
            case 'weekdays':
                this.model.freq = 'WEEKLY';
                this.model.byday = ['MO', 'TU', 'WE', 'TH', 'FR'];
                break;
            case 'custom':
                // Will be set by custom controls
                break;
        }
    }

    toggleWeekday(day: any): void {
        day.selected = !day.selected;

        // Update model
        if (day.selected && !this.model.byday.includes(day.value)) {
            this.model.byday.push(day.value);
        } else if (!day.selected) {
            this.model.byday = this.model.byday.filter(d => d !== day.value);
        }
    }

    resetWeekdaySelection(): void {
        this.weekdayButtons.forEach(day => day.selected = false);
        this.model.byday = [];
    }

    // Update model when custom frequency changes
    onCustomFrequencyChange(): void {
        if (this.selectedRecurrence === 'custom') {
            switch (this.customFrequency) {
                case 'day':
                    this.model.freq = 'DAILY';
                    break;
                case 'week':
                    this.model.freq = 'WEEKLY';
                    break;
                case 'month':
                    this.model.freq = 'MONTHLY';
                    break;
                case 'year':
                    this.model.freq = 'YEARLY';
                    break;
            }
            this.model.interval = this.customInterval;
        }
    }

    // Generate RRULE string from current settings
    generateRRule(): string {
        if (this.selectedRecurrence === 'none' || !this.model.freq) {
            return '';
        }

        let rrule = `FREQ=${this.model.freq}`;

        if (this.model.interval > 1) {
            rrule += `;INTERVAL=${this.model.interval}`;
        }

        if (this.model.byday.length > 0) {
            rrule += `;BYDAY=${this.model.byday.join(',')}`;
        }

        if (this.model.wkst && this.model.wkst !== 'MO') {
            rrule += `;WKST=${this.model.wkst}`;
        }

        // Add end conditions
        if (this.endsOption === 'on' && this.endDate) {
            const endDateStr = this.endDate.toISOString().split('T')[0].replace(/-/g, '');
            rrule += `;UNTIL=${endDateStr}T235959Z`;
        } else if (this.endsOption === 'after' && this.occurrences > 0) {
            rrule += `;COUNT=${this.occurrences}`;
        }

        return `RRULE:${rrule}`;
    }

    save(): void {
        this.isLoading = true;

        // Update custom interval if in custom mode
        if (this.selectedRecurrence === 'custom') {
            this.model.interval = this.customInterval;
            this.onCustomFrequencyChange();
        }

        // Generate final RRULE
        const rruleString = this.generateRRule();
        if (rruleString) {
            this.model.customRrule = rruleString;
        }

        // Create comprehensive recurrence data
        const recurrenceData = this.getRecurrenceData();

        setTimeout(() => {
            this.isLoading = false;
            this.dialogRef.close(recurrenceData); // Return the full object instead of just this.model
        }, 500);
    }

    cancel(): void {
        this.dialogRef.close();
    }

    closeDialog(): void {
        this.cancel();
    }

    // Original method preserved for compatibility
    toggleDay(day: string, checked: boolean): void {
        const list = this.model.byday;
        if (checked && !list.includes(day)) list.push(day);
        if (!checked) this.model.byday = list.filter(d => d !== day);
    }

    getFormattedBaseDate(): string {
        const day = this.baseDate.getDate().toString().padStart(2, '0');
        const month = (this.baseDate.getMonth() + 1).toString().padStart(2, '0');
        const year = this.baseDate.getFullYear();
        return `${day}/${month}/${year}`;
    }

    getRecurrenceData(): any {
        // Clean data - ensure original_record_id is not included
		
		let normalizedEndDate = this.endDate;
		if (this.endDate) {
			// Create a new date at noon UTC to avoid timezone boundary issues
			normalizedEndDate = new Date(Date.UTC(
				this.endDate.getFullYear(),
				this.endDate.getMonth(),
				this.endDate.getDate(),
				12, 0, 0
			));
		}
	
        const cleanRecurrenceData = {
            // Original model data
            model: this.model,

            // Google-style interface data
            interface: {
                selectedRecurrence: this.selectedRecurrence,
                endsOption: this.endsOption,
                endDate: normalizedEndDate,
                occurrences: this.occurrences,
                customInterval: this.customInterval,
                customFrequency: this.customFrequency
            },

            // Generated RRULE
            rrule: this.generateRRule(),

            // Base date used for calculations
            baseDate: this.baseDate,

            // Human readable description
            description: this.getHumanReadableDescription()
        };

        // Explicitly ensure original_record_id is not present
        delete (cleanRecurrenceData as any).original_record_id;

        return cleanRecurrenceData;
    }

    private getHumanReadableDescription(): string {
        if (this.selectedRecurrence === 'none') {
            return 'Does not repeat';
        }

        let description = '';

        // Build the main recurrence pattern
        switch (this.selectedRecurrence) {
            case 'daily':
                description = 'Daily';
                break;

            case 'weekly':
                description = `Weekly on ${this.getDayName()}s`;
                break;

            case 'monthly':
                description = `Monthly on the ${this.getOrdinalDay()}`;
                break;

            case 'annually':
                description = `Annually on ${this.getMonthDay()}`;
                break;

            case 'weekdays':
                description = 'Every weekday (Monday to Friday)';
                break;

            case 'custom':
                description = this.getCustomRecurrenceDescription();
                break;
        }

        // Add starting date
        description += ` starting ${this.getFormattedBaseDate()}`;

        // Add end condition
        switch (this.endsOption) {
            case 'on':
                if (this.endDate) {
                    const endDateStr = this.formatDate(this.endDate);
                    description += ` until ${endDateStr}`;
                }
                break;

            case 'after':
                const occurrenceText = this.occurrences === 1 ? 'occurrence' : 'occurrences';
                description += ` for ${this.occurrences} ${occurrenceText}`;
                break;

            // 'never' - no additional text needed
        }

        return description;
    }

    private getCustomRecurrenceDescription(): string {
        let description = '';

        if (this.customFrequency === 'week' && this.model.byday.length > 0) {
            // Special handling for weekly with specific days
            const dayMap = {
                'MO': 'Monday',
                'TU': 'Tuesday',
                'WE': 'Wednesday',
                'TH': 'Thursday',
                'FR': 'Friday',
                'SA': 'Saturday',
                'SU': 'Sunday'
            };

            const selectedDays = this.model.byday.map(day => dayMap[day] || day);

            if (selectedDays.length === 1) {
                description = `Every ${this.customInterval > 1 ? this.customInterval + ' weeks' : 'week'} on ${selectedDays[0]}`;
            } else if (selectedDays.length > 1) {
                const lastDay = selectedDays.pop();
                description = `Every ${this.customInterval > 1 ? this.customInterval + ' weeks' : 'week'} on ${selectedDays.join(', ')} and ${lastDay}`;
            }
        } else {
            // Standard custom description
            const frequencyText = this.customInterval === 1
                ? `Every ${this.customFrequency}`
                : `Every ${this.customInterval} ${this.customFrequency}s`;
            description = frequencyText;
        }

        return description;
    }

    private formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        // Get the PHP format from the field configuration
        const phpFormat = this.data.field.recurringDataPrimaryDateFieldFormat || 'd/m/Y';

        // Format according to the PHP format
        switch (phpFormat) {
            case 'd/m/Y':
                return `${day}/${month}/${year}`;
            case 'm/d/Y':
                return `${month}/${day}/${year}`;
            case 'Y-m-d':
                return `${year}-${month}-${day}`;
            default:
                return `${day}/${month}/${year}`;
        }
    }

    onBaseDateChange(): void {  
        if (this.baseDate) {
            this.baseDate = new Date(
                this.baseDate.getFullYear(),
                this.baseDate.getMonth(),
                this.baseDate.getDate(),
                12, 0, 0
            );
            console.log('Base date changed to:', this.baseDate);
        }
    }
}